'use client'

import { Loader2, SlidersHorizontal, X } from 'lucide-react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useCatalogFilters } from '@/features/products-catalog/hooks/use-catalog-filters'
import { useProductsCatalog } from '@/features/products-catalog/hooks/use-products-catalog'
import {
  buildCatalogQuery,
  CATALOG_URL_PARAMS,
  DEFAULT_CATALOG_SORT,
} from '@/features/products-catalog/search-params'
import {
  type FilterableProductType,
  PRODUCT_TYPE_QUERY_KEY,
  resolveProductType,
} from '@/helpers/products-filter-type'
import { cn } from '@/lib/utils'
import {
  getContentTypes,
  MobileAppliedFilters,
  ProductsFilter,
  SectionLabel,
} from './products-filter'
import { SORT_OPTIONS } from './select-sort'

const CATALOG_ROOT_REGEX = /^\/cursos/

/**
 * Widgets flutuantes de terceiros (chat, VLibras) com z-index acima do
 * painel; ficam escondidos enquanto ele está aberto para não cobrir o botão.
 */
const FLOATING_WIDGETS_SELECTOR = [
  '#atalia-webchat-host',
  '#vlibras-access-wrapper',
  '[vw]',
  '.woot-widget-bubble',
].join(',')

function useHideFloatingWidgets(hidden: boolean) {
  useEffect(() => {
    if (!hidden) {
      return
    }
    const widgets = [
      ...document.querySelectorAll<HTMLElement>(FLOATING_WIDGETS_SELECTOR),
    ]
    for (const widget of widgets) {
      widget.style.visibility = 'hidden'
    }
    return () => {
      for (const widget of widgets) {
        widget.style.visibility = ''
      }
    }
  }, [hidden])
}

const RESULT_NOUNS: Record<
  FilterableProductType,
  { singular: string; plural: string }
> = {
  course: { singular: 'curso', plural: 'cursos' },
  postgraduate: { singular: 'pós-graduação', plural: 'pós-graduações' },
  track: { singular: 'trilha', plural: 'trilhas' },
  webinar: { singular: 'webinar', plural: 'webinars' },
}

/**
 * Altura do cabeçalho fixo do site, para a barra grudar logo abaixo dele.
 * Retorna 0 quando o cabeçalho não é fixo.
 */
function useStickyHeaderOffset() {
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    // O cabeçalho pode chegar depois (streaming) e mudar de altura; mede de
    // novo sempre que o documento muda de tamanho.
    function measure() {
      const header = document.querySelector<HTMLElement>('body > header')
      const position = header ? getComputedStyle(header).position : ''
      const isPinned = position === 'sticky' || position === 'fixed'
      setOffset(header && isPinned ? header.offsetHeight : 0)
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(document.body)
    return () => observer.disconnect()
  }, [])

  return offset
}

/**
 * Total de resultados da URL atual — mesma query (e cache) da listagem, para
 * o botão do painel mostrar quantos itens o filtro retorna.
 */
function useCurrentResultsCount() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const segments = pathname
    .replace(CATALOG_ROOT_REGEX, '')
    .split('/')
    .filter(Boolean)
    .map(segment => decodeURIComponent(segment))
  const areaSlug = segments.at(0) ?? ''

  const query = buildCatalogQuery({
    searchParams,
    productType: resolveProductType({
      slug: areaSlug,
      type: searchParams.get(PRODUCT_TYPE_QUERY_KEY),
    }),
    areaSlug,
    categorySlug: segments.at(1) ?? '',
  })
  const { data, isFetching } = useProductsCatalog(query)

  return { total: data?.pagination?.total_results, isFetching }
}

function SortOptions() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const currentSort =
    searchParams.get(CATALOG_URL_PARAMS.sort) ?? DEFAULT_CATALOG_SORT

  function handleSelect(sort: string) {
    const params = new URLSearchParams(searchParams.toString())
    params.set(CATALOG_URL_PARAMS.sort, sort)
    params.delete(CATALOG_URL_PARAMS.page)
    router.push(`?${params.toString()}`, { scroll: false })
  }

  return (
    <div className="border-gray-100 border-b pb-4">
      <SectionLabel>Ordenar por</SectionLabel>
      <div className="mt-2 flex flex-wrap gap-2">
        {SORT_OPTIONS.map(option => {
          const isSelected = option.value === currentSort
          return (
            <button
              aria-pressed={isSelected}
              className={cn(
                'cursor-pointer rounded-full border px-3.5 py-2 text-sm transition-colors',
                isSelected
                  ? 'border-primary-600 bg-primary-50 font-semibold text-primary-700'
                  : 'border-gray-200 text-gray-700 active:bg-gray-50'
              )}
              key={option.value}
              onClick={() => handleSelect(option.value)}
              type="button"
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface MobileProductsToolbarProps {
  hasTrack: boolean
  hasPostgraduate: boolean
  workloads: string[]
}

export function MobileProductsToolbar({
  hasTrack,
  hasPostgraduate,
  workloads,
}: MobileProductsToolbarProps) {
  const { filters, isPending, updateFilters, clearFilters } =
    useCatalogFilters()
  const { total, isFetching } = useCurrentResultsCount()
  const stickyTop = useStickyHeaderOffset()
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  useHideFloatingWidgets(isSheetOpen)
  const typeListRef = useRef<HTMLUListElement>(null)

  // Mantém o tipo ativo visível na lista com rolagem horizontal.
  useEffect(() => {
    const list = typeListRef.current
    const active = list?.querySelector<HTMLElement>(
      `[data-value="${filters.productType}"]`
    )
    if (!(list && active)) {
      return
    }
    list.scrollTo({
      left: active.offsetLeft - (list.clientWidth - active.offsetWidth) / 2,
      behavior: 'smooth',
    })
  }, [filters.productType])

  const contentTypes = getContentTypes({ hasTrack, hasPostgraduate })
  // O tipo já aparece nos chips da barra; o contador soma só o resto.
  const activeCount = [
    filters.areaSlug,
    filters.categorySlug,
    filters.workload,
  ].filter(Boolean).length

  const isLoadingCount = isPending || isFetching || total === undefined
  const noun = RESULT_NOUNS[filters.productType]
  let resultsLabel = `Ver ${total} ${total === 1 ? noun.singular : noun.plural}`
  if (isLoadingCount) {
    resultsLabel = 'Carregando…'
  } else if (total === 0) {
    resultsLabel = 'Nenhum resultado'
  }

  // Fragmento: a barra precisa ser filha direta do <main> para o `sticky`
  // acompanhar a rolagem da página inteira.
  return (
    <>
      <div
        className="sticky z-30 border-gray-100 border-b bg-white py-3 md:hidden"
        style={{ top: stickyTop }}
      >
        <div className="flex items-center gap-2">
          <Sheet onOpenChange={setIsSheetOpen} open={isSheetOpen}>
            <SheetTrigger asChild>
              <button
                className={cn(
                  'flex h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-3.5 font-semibold text-sm transition-colors',
                  activeCount > 0
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-gray-300 text-gray-800 active:bg-gray-50'
                )}
                id="filter-mb"
                type="button"
              >
                <SlidersHorizontal className="size-4" />
                Filtros
                {activeCount > 0 && (
                  <span className="flex size-5 items-center justify-center rounded-full bg-white font-bold text-primary-700 text-xs">
                    {activeCount}
                  </span>
                )}
              </button>
            </SheetTrigger>

            <SheetContent
              className="flex max-h-[85dvh] flex-col gap-0 rounded-t-2xl p-0"
              onOpenAutoFocus={event => event.preventDefault()}
              side="bottom"
            >
              <div className="flex items-center justify-between border-gray-100 border-b px-5 py-3">
                <SheetTitle className="font-bold text-gray-900 text-lg">
                  Filtros
                </SheetTitle>
                <SheetClose
                  aria-label="Fechar filtros"
                  className="-mr-2 cursor-pointer rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
                >
                  <X className="size-5" />
                </SheetClose>
              </div>

              <div className="flex-1 overflow-y-auto px-5 pt-4 pb-6">
                <SortOptions />
                <ProductsFilter
                  hasPostgraduate={hasPostgraduate}
                  hasTrack={hasTrack}
                  variant="sheet"
                  workloads={workloads}
                />
              </div>

              <SheetFooter className="flex-row gap-3 border-gray-100 border-t bg-white p-4 sm:space-x-0">
                <Button
                  className="h-12 flex-1"
                  disabled={activeCount === 0}
                  onClick={clearFilters}
                  type="button"
                  variant="outline"
                >
                  Limpar
                </Button>
                <SheetClose asChild>
                  <Button className="h-12 flex-[2] gap-2" type="button">
                    {isLoadingCount && (
                      <Loader2 className="size-4 animate-spin" />
                    )}
                    {resultsLabel}
                  </Button>
                </SheetClose>
              </SheetFooter>
            </SheetContent>
          </Sheet>

          {contentTypes.length > 1 && (
            <>
              <span aria-hidden="true" className="h-6 w-px bg-gray-200" />
              <ul
                className="relative flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                ref={typeListRef}
              >
                {contentTypes.map(({ value, label, icon: Icon }) => {
                  const isSelected = value === filters.productType
                  return (
                    <li className="shrink-0" key={value}>
                      <button
                        aria-pressed={isSelected}
                        className={cn(
                          'flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 font-medium text-sm transition-colors',
                          isSelected
                            ? 'border-primary-600 bg-primary-50 text-primary-700'
                            : 'border-gray-200 text-gray-700 active:bg-gray-50'
                        )}
                        data-value={value}
                        onClick={() => updateFilters({ productType: value })}
                        type="button"
                      >
                        <Icon className="size-4" />
                        {label}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </>
          )}
        </div>
      </div>

      <div className="md:hidden">
        <MobileAppliedFilters
          hasPostgraduate={hasPostgraduate}
          hasTrack={hasTrack}
        />
      </div>
    </>
  )
}
